/**
 * The house-cleaning table off the published card, read only.
 *
 * There is no price input anywhere in this product and there never will be. CLAUDE.md seam rule 1:
 * a price changes on faithfullykept.com first, this system copies it, content-hashes it, and a
 * nightly check fails loudly when the two disagree. So the only thing an interface may do with a
 * price is SHOW it, which is what this does: Victoria on the phone to a prospect can read the
 * figure straight off the card the customer is looking at.
 *
 * Everything below comes from GET /api/catalog. No figure, no row label and no column heading is
 * written into this file. A number nobody can type is a number nobody can quietly change.
 */
import type { RateLine } from '@fk/shared';
import { money } from '../format.ts';

const HOUSE_CLEANING = 'House cleaning, per visit';

function cell(line: RateLine | undefined): string {
  if (!line) return '';
  if (line.priceCents === null) return 'Quoted';
  return `${line.isFrom ? 'From ' : ''}${money(line.priceCents)}`;
}

export function PublishedRates({
  lines,
  sourceUrl,
  highlightRowLabel,
}: {
  lines: readonly RateLine[];
  sourceUrl: string;
  /** The row matching the home being typed in, when the card publishes one for it. */
  highlightRowLabel: string | null;
}) {
  const house = lines.filter((line) => line.source === HOUSE_CLEANING);
  const rowLabels = [...new Set(house.map((line) => line.rowLabel))];
  const columnLabels = [...new Set(house.map((line) => line.columnLabel))];
  const at = (rowLabel: string, columnLabel: string) =>
    house.find((line) => line.rowLabel === rowLabel && line.columnLabel === columnLabel);

  return (
    <div>
      <p className="mb-2 text-xs text-navy/65">
        Read from the published card at {sourceUrl}. Nothing here is typed and nothing here can be
        edited: the page is the contract, and a nightly check fails when this copy drifts from it.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="border-b border-navy/20 px-2 py-2 text-left font-medium">Home</th>
              {columnLabels.map((columnLabel) => (
                <th
                  key={columnLabel}
                  className="border-b border-navy/20 px-2 py-2 text-right font-medium whitespace-nowrap"
                >
                  {columnLabel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowLabels.map((rowLabel) => (
              <tr
                key={rowLabel}
                className={rowLabel === highlightRowLabel ? 'bg-gold/15 font-medium' : ''}
              >
                <td className="border-b border-navy/10 px-2 py-2 whitespace-nowrap">{rowLabel}</td>
                {columnLabels.map((columnLabel) => (
                  <td
                    key={columnLabel}
                    className="border-b border-navy/10 px-2 py-2 text-right whitespace-nowrap"
                  >
                    {cell(at(rowLabel, columnLabel))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Which published row a home of this size falls on, or null when the card does not publish one.
 * Matched by the card's own line codes rather than by a table of sizes copied into this file: the
 * codes come back from the catalog, so a card revision that adds a row needs no change here.
 */
export function rowLabelForHome(
  lines: readonly RateLine[],
  bedrooms: number | null,
  bathrooms: number | null,
): string | null {
  if (bedrooms === null || bathrooms === null) return null;
  const code = `clean.${bedrooms}b${bathrooms}b.`;
  const match = lines.find((line) => line.code.startsWith(code));
  return match ? match.rowLabel : null;
}

/**
 * The house-cleaning line the card publishes for a home of this shape at this cadence, or null
 * when the card has no such row and a person has to choose one. The same rule as the server's
 * `schedule/pricing.ts` houseLineCode, so what the screen shows is what the server will price:
 * the card's top row is "5 bed / 3+ bath", so three or more bathrooms on a five-bedroom home is
 * that row, and six bedrooms and up is the "6+ bed" row, which is quoted rather than priced.
 */
export function houseLineForHome(
  lines: readonly RateLine[],
  bedrooms: number | null,
  bathrooms: number | null,
  cadence: string,
): RateLine | null {
  if (bedrooms === null || bathrooms === null) return null;
  const code =
    bedrooms >= 6
      ? `clean.6plus.${cadence}`
      : `clean.${bedrooms}b${bedrooms === 5 ? Math.min(bathrooms, 3) : bathrooms}b.${cadence}`;
  return lines.find((line) => line.code === code) ?? null;
}

/** The prefixes each service line is priced from. Copied from the server, which refuses any other. */
const LINE_PREFIXES: Record<string, readonly string[]> = {
  house_cleaning: ['clean.', 'deep.', 'moveout.'],
  home_watch: ['watch.'],
  turnover: ['turn.'],
};

/**
 * The card lines a person may choose from for a service. For house cleaning the recurring rows are
 * narrowed to the chosen cadence, because cadence sets the price and the server refuses a line
 * priced for another column; the one-off deep clean and move-out rows have no cadence.
 */
export function linesForService(
  lines: readonly RateLine[],
  serviceLine: string,
  cadence: string,
): readonly RateLine[] {
  const prefixes = LINE_PREFIXES[serviceLine] ?? [];
  return lines.filter(
    (line) =>
      prefixes.some((prefix) => line.code.startsWith(prefix)) &&
      (!line.code.startsWith('clean.') || line.code.endsWith(`.${cadence}`)),
  );
}

/** "3 bed / 2 bath, Every 2 weeks, $180.00", or "Quoted" where the card says so. */
export function describeLine(line: RateLine): string {
  const figure =
    line.priceCents === null
      ? 'quoted after a walkthrough'
      : `${line.isFrom ? 'from ' : ''}${money(line.priceCents)}`;
  return `${line.rowLabel}, ${line.columnLabel}, ${figure}`;
}
